<template>
  <div class="sms-records">
    <!-- 查询条件 -->
    <vab-query-form>
      <vab-query-form-right-panel :span="24">
        <el-form inline @submit.prevent="query(1)">
          <el-form-item label="主题">
            <el-input v-model="queryParams.subject" clearable placeholder="请输入" />
          </el-form-item>
          <el-form-item label="短信内容">
            <el-input v-model="queryParams.content" clearable placeholder="请输入" />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="queryParams.sendStatus" clearable placeholder="请选择">
              <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="发送时间">
            <el-date-picker
              v-model="dateRange"
              clearable
              end-placeholder="结束日期"
              start-placeholder="开始日期"
              type="daterange"
              value-format="YYYY-MM-DD"
            />
          </el-form-item>
          <el-form-item>
            <el-button icon="search" type="primary" @click="query(1)">查询</el-button>
            <el-button icon="download" :loading="exporting" type="primary" @click="handleExport">导出</el-button>
            <el-button :disabled="!selectedPendingIds.length" icon="close" type="warning" @click="handleBatchCancel">
              取消定时
            </el-button>
          </el-form-item>
        </el-form>
      </vab-query-form-right-panel>
    </vab-query-form>

    <!-- 表格 -->
    <el-table
      v-loading="loading"
      border
      :data="tableData"
      :table-layout="'auto'"
      @selection-change="handleSelectionChange"
    >
      <el-table-column align="center" type="selection" width="50" />
      <el-table-column label="主题" prop="subject" show-overflow-tooltip />
      <el-table-column label="短信内容" prop="content" show-overflow-tooltip />
      <el-table-column label="发送号码" prop="phones" show-overflow-tooltip />
      <el-table-column align="center" label="计费" prop="billingCount" width="80" />
      <el-table-column align="center" label="状态" prop="statusName" width="120" />
      <el-table-column label="提交时间" prop="createTime" width="180" />
      <el-table-column label="发送时间" prop="sendTime" width="180" />
      <el-table-column label="定时发送" width="180">
        <template #default="{ row }">
          {{ row.scheduleTime || (row.sendStatus === 'SUCCESS' ? '否' : '') }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" width="100">
        <template #default="{ row }">
          <el-link v-if="row.sendStatus === 'PENDING'" type="danger" :underline="false" @click="handleCancelOne(row)">
            取消定时
          </el-link>
        </template>
      </el-table-column>
      <template #empty>
        <el-empty class="vab-data-empty" description="暂无数据" />
      </template>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-model:current-page="queryParams.pageNum"
      background
      layout="prev, pager, next"
      :page-size="queryParams.pageSize"
      :total="queryParams.total"
      @current-change="query"
    />
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
  import { querySmsRecordPage, exportSmsRecord, cancelScheduledSms } from '@/api/sms'
  import { omit } from 'lodash'

  const { proxy } = getCurrentInstance()

  const statusOptions = [
    { label: '已发送', value: 'SUCCESS' },
    { label: '定时未发送', value: 'PENDING' },
    { label: '发送失败', value: 'FAIL' },
    { label: '已取消', value: 'CANCELLED' },
  ]

  const defaultQueryParams = {
    subject: '',
    content: '',
    sendStatus: '',
    pageNum: 1,
    pageSize: 20,
    total: 0,
  }

  const queryParams = reactive({ ...defaultQueryParams })
  const dateRange = ref(null)
  const loading = ref(false)
  const tableData = ref([])
  const selectedRows = ref([])

  const selectedPendingIds = computed(() =>
    selectedRows.value.filter((r) => r.sendStatus === 'PENDING').map((r) => r.id)
  )

  onMounted(() => {
    query()
  })

  function query(pageNum = queryParams.pageNum) {
    const params = {
      ...omit(queryParams, 'total'),
      pageNum,
    }
    if (dateRange.value?.length === 2) {
      params.startDate = dateRange.value[0]
      params.endDate = dateRange.value[1]
    }

    loading.value = true
    querySmsRecordPage(params)
      .then(({ code, data }) => {
        if (code !== 200) return
        const { list = [], total = 0 } = data || {}
        tableData.value = list
        queryParams.total = total
      })
      .finally(() => {
        loading.value = false
      })
  }

  function handleSelectionChange(rows) {
    selectedRows.value = rows
  }

  // ---- 导出 ----
  const exporting = ref(false)

  function handleExport() {
    const params = {
      ...omit(queryParams, 'total'),
    }
    if (dateRange.value?.length === 2) {
      params.startDate = dateRange.value[0]
      params.endDate = dateRange.value[1]
    }

    exporting.value = true
    exportSmsRecord(params)
      .then(({ code, data }) => {
        if (code !== 200) return
        if (data?.fileUrl) {
          window.open(data.fileUrl)
        }
      })
      .finally(() => {
        exporting.value = false
      })
  }

  // ---- 取消定时 ----
  function handleBatchCancel() {
    if (!selectedPendingIds.value.length) return
    proxy.$baseConfirm(`确定取消所选的 ${selectedPendingIds.value.length} 条定时发送吗？`, null, () => {
      doCancelScheduled(selectedPendingIds.value)
    })
  }

  function handleCancelOne(row) {
    proxy.$baseConfirm('确定取消该条定时发送吗？', null, () => {
      doCancelScheduled([row.id])
    })
  }

  function doCancelScheduled(ids) {
    cancelScheduledSms(ids).then(({ code }) => {
      if (code !== 200) return
      proxy.$baseMessage('操作成功', 'success', 'vab-hey-message-success')
      query()
    })
  }
</script>

<style lang="scss" scoped>
  .sms-records {
    padding: 16px;
  }
</style>
